import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

import { getEmployeePermissions, type Permission } from 'src/api/permissionApi';
import { PermissionStatus } from 'src/sections/permission/enum/PermissionStatus';
import { PermissionType } from 'src/sections/permission/enum/PermissionType';

interface EmployeeLeaveTabProps {
  employeeId: string;
}

export default function EmployeeLeaveTab({ employeeId }: EmployeeLeaveTabProps) {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getEmployeePermissions(employeeId);
        setPermissions(data);
      } catch (err: any) {
        setError(err.message || 'İzin talepleri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [employeeId]);

  const getStatusColor = (status: PermissionStatus) => {
    switch (status) {
      case PermissionStatus.APPROVED:
        return 'success';
      case PermissionStatus.PENDING:
        return 'warning';
      case PermissionStatus.REJECTED:
        return 'error';
      case PermissionStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: PermissionStatus) => {
    switch (status) {
      case PermissionStatus.APPROVED:
        return 'Onaylandı';
      case PermissionStatus.PENDING:
        return 'Beklemede';
      case PermissionStatus.REJECTED:
        return 'Reddedildi';
      case PermissionStatus.CANCELLED:
        return 'İptal Edildi';
      default:
        return status;
    }
  };

  const getLeaveTypeLabel = (type: PermissionType) => {
    switch (type) {
      case PermissionType.ANNUAL:
        return 'Yıllık İzin';
      case PermissionType.SICK:
        return 'Hastalık İzni';
      case PermissionType.MATERNITY:
        return 'Doğum İzni';
      case PermissionType.PATERNITY:
        return 'Babalık İzni';
      case PermissionType.UNPAID:
        return 'Ücretsiz İzin';
      case PermissionType.MARRIAGE:
        return 'Evlilik İzni';
      case PermissionType.BEREAVEMENT:
        return 'Ölüm İzni';
      default:
        return type;
    }
  };

  const calculateDays = (startDate: string, endDate: string) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Include both start and end date
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (permissions.length === 0) {
    return (
      <Alert severity="info">
        Bu çalışana ait izin talebi bulunmamaktadır.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          İzin Talepleri ({permissions.length})
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>İzin Türü</TableCell>
              <TableCell>Başlangıç</TableCell>
              <TableCell>Bitiş</TableCell>
              <TableCell>Gün Sayısı</TableCell>
              <TableCell>Sebep</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Talep Tarihi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((permission) => (
              <TableRow key={permission.id} hover>
                <TableCell>
                  <Chip
                    label={getLeaveTypeLabel(permission.leaveType)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {format(parseISO(permission.startDate), 'dd MMM yyyy', { locale: tr })}
                </TableCell>
                <TableCell>
                  {format(parseISO(permission.endDate), 'dd MMM yyyy', { locale: tr })}
                </TableCell>
                <TableCell>
                  {calculateDays(permission.startDate, permission.endDate)} gün
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap title={permission.reason}>
                    {permission.reason}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(permission.status)}
                    color={getStatusColor(permission.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(parseISO(permission.createdAt), 'dd MMM yyyy', { locale: tr })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
